/*
 * ScheduleGrid building blocks — types, helpers, and sub-components that the
 * staff /schedule page uses to render the day/week/month/list views.
 *
 * Extracted from pages/staff/schedule.tsx as the first step of the
 * INTEGRATION_PLAN.md Phase-2 staff-schedule migration (pure split, no
 * behavior change). Follow-up commits will assemble a top-level
 * <ScheduleGrid> component in this file and route the page through it.
 */

import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import {
  AlertCircle,
  Ban,
  Check,
  CheckCircle,
  ChevronRight,
  Clock,
  Layers,
  Lock,
  Package,
  Trash2,
  UserCheck,
  Users,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import type { StaffAvailableSlot } from '@/services/api/staff.service';

// ── Constants ──────────────────────────────────────────────────────────

/** Pixels per 30-min slot in the day/week time-grid layout. */
export const ROW_HEIGHT = 40;
/** Earliest hour shown on the time-grid. */
export const START_HOUR = 6;
/** Latest hour shown on the time-grid. */
export const END_HOUR = 22;

/** Pre-computed "HH:MM" labels for every 30-min slot between START_HOUR and END_HOUR. */
export const TIME_SLOTS = Array.from({ length: (END_HOUR - START_HOUR) * 2 }, (_, i) => {
  const hour = START_HOUR + Math.floor(i / 2);
  const minute = (i % 2) * 30;
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
});

// ── Types ──────────────────────────────────────────────────────────────

/** Minimal shape for the overlap helper — works for both staff and admin slots. */
export interface SlotLike {
  id: number;
  start_time?: string | null;
  end_time?: string | null;
  available: boolean;
  booked: boolean;
  booking?: { status?: string } | null;
  booking_status?: string;
  provider_id?: number;
}

/** Consecutive identical slots merged into a single rendered block. */
export interface MergedTimeBlock {
  startTime: string;      // "09:00"
  endTime: string;        // "11:00"
  type: 'booked' | 'available' | 'blocked' | 'group_class';
  slots: StaffAvailableSlot[];
  serviceCount?: number;
  customerName?: string;
  serviceName?: string;
  // Group-class capacity counter — only set for type='group_class'.
  currentBookings?: number;
  maxCapacity?: number;
  cancelled?: boolean;
}

/** A block with horizontal-lane info for Google-Calendar-style overlap rendering. */
export interface PositionedBlock extends MergedTimeBlock {
  column: number;
  totalColumns: number;
}

// ── Helpers (public) ───────────────────────────────────────────────────

/** Return all slots that overlap the given slot's time range. */
export const findOverlappingSlots = <T extends SlotLike>(slot: T, allSlots: T[]): T[] => {
  if (!slot.start_time || !slot.end_time) return [];
  const slotStart = new Date(slot.start_time).getTime();
  const slotEnd = new Date(slot.end_time).getTime();

  return allSlots.filter(other => {
    if (other.id === slot.id) return false;
    if (!other.start_time || !other.end_time) return false;
    // Skip provider check if provider_id is not available (staff slots don't have it)
    if (slot.provider_id !== undefined && other.provider_id !== undefined && other.provider_id !== slot.provider_id) return false;

    const otherStart = new Date(other.start_time).getTime();
    const otherEnd = new Date(other.end_time).getTime();

    return slotStart < otherEnd && slotEnd > otherStart;
  });
};

/** Group slots by their wall-clock start time ("HH:MM"). */
export const groupSlotsByStartTime = (slots: StaffAvailableSlot[]) => {
  const groups = new Map<string, StaffAvailableSlot[]>();

  slots.forEach(slot => {
    if (!slot.start_time) return;
    const startTime = format(parseISO(slot.start_time), 'HH:mm');
    if (!groups.has(startTime)) {
      groups.set(startTime, []);
    }
    groups.get(startTime)!.push(slot);
  });

  return Array.from(groups.entries())
    .sort(([timeA], [timeB]) => timeA.localeCompare(timeB))
    .map(([time, slots]) => ({ time, slots }));
};

/** Merge consecutive identical time blocks (e.g. two consecutive booked slots for
 *  the same customer + service collapse into one block). */
export const mergeConsecutiveBlocks = (
  timeGroups: { time: string; slots: StaffAvailableSlot[] }[]
): MergedTimeBlock[] => {
  if (timeGroups.length === 0) return [];

  const merged: MergedTimeBlock[] = [];

  for (const group of timeGroups) {
    const currentBlock = getBlockInfo(group);
    const lastMerged = merged[merged.length - 1];

    if (lastMerged && canMergeBlocks(lastMerged, currentBlock)) {
      // Extend the previous block's end time
      lastMerged.endTime = currentBlock.endTime;
      lastMerged.slots.push(...group.slots);
    } else {
      merged.push(currentBlock);
    }
  }

  return merged;
};

/** Assign column lanes to overlapping blocks (Google-Calendar style). */
export const calculateBlockPositions = (blocks: MergedTimeBlock[]): PositionedBlock[] => {
  if (blocks.length === 0) return [];

  // Sort by start time, then by end time (longer blocks first).
  const sorted = [...blocks].sort((a, b) => {
    const aStart = timeToMinutes(a.startTime);
    const bStart = timeToMinutes(b.startTime);
    if (aStart !== bStart) return aStart - bStart;
    return timeToMinutes(b.endTime) - timeToMinutes(a.endTime);
  });

  const positioned: PositionedBlock[] = [];
  const columns: MergedTimeBlock[][] = [];

  for (const block of sorted) {
    // Find the first column where this block doesn't overlap.
    let assignedColumn = -1;
    for (let col = 0; col < columns.length; col++) {
      const columnBlocks = columns[col];
      const hasOverlap = columnBlocks.some(cb => blocksOverlap(cb, block));
      if (!hasOverlap) {
        assignedColumn = col;
        break;
      }
    }

    if (assignedColumn === -1) {
      assignedColumn = columns.length;
      columns.push([]);
    }

    columns[assignedColumn].push(block);
    positioned.push({
      ...block,
      column: assignedColumn,
      totalColumns: 0, // calculated in next pass
    });
  }

  // Compute totalColumns for each block from its overlap group.
  for (let i = 0; i < positioned.length; i++) {
    const block = positioned[i];
    const overlappingBlocks = positioned.filter(b => blocksOverlap(block, b));
    const maxColumn = Math.max(...overlappingBlocks.map(b => b.column));
    positioned[i].totalColumns = maxColumn + 1;
  }

  return positioned;
};

/** CSS style for a PositionedBlock — top/height from time, left/width from lane. */
export const getPositionedBlockStyle = (block: PositionedBlock): React.CSSProperties => {
  const [startHour, startMin] = block.startTime.split(':').map(Number);
  const [endHour, endMin] = block.endTime.split(':').map(Number);

  const startSlots = (startHour - START_HOUR) * 2 + (startMin / 30);
  const endSlots = (endHour - START_HOUR) * 2 + (endMin / 30);
  const durationSlots = endSlots - startSlots;

  const widthPercent = 100 / block.totalColumns;
  const leftPercent = block.column * widthPercent;

  return {
    top: `${startSlots * ROW_HEIGHT}px`,
    height: `${Math.max(durationSlots, 1) * ROW_HEIGHT}px`,
    left: `${leftPercent}%`,
    width: `${widthPercent}%`,
  };
};

// ── Helpers (private) ──────────────────────────────────────────────────

/** Group-class detector — capacity > 1 means a class instance, not 1:1. */
export const isGroupClassSlot = (slot: StaffAvailableSlot): boolean => {
  const cap = slot.max_capacity ?? slot.service?.max_capacity ?? 1;
  return cap > 1;
};

/** Project a time-group into a MergedTimeBlock with type + display fields. */
const getBlockInfo = (group: { time: string; slots: StaffAvailableSlot[] }): MergedTimeBlock => {
  const { time, slots } = group;
  const bookedSlot = slots.find(s => s.booked);
  const availableSlots = slots.filter(s => s.available && !s.booked);

  const endTimes = slots
    .filter(s => s.end_time)
    .map(s => format(parseISO(s.end_time!), 'HH:mm'));
  const endTime = endTimes.length > 0 ? endTimes.sort().pop()! : time;

  // Group-class slots take priority — a class with 1/10 bookings is still a
  // class block, not a "booked" 1:1 slot.
  const groupClassSlot = slots.find(isGroupClassSlot);
  if (groupClassSlot) {
    return {
      startTime: time,
      endTime,
      type: 'group_class',
      slots,
      serviceName: groupClassSlot.service?.name || '',
      currentBookings: groupClassSlot.current_bookings ?? 0,
      maxCapacity: groupClassSlot.max_capacity ?? groupClassSlot.service?.max_capacity ?? 0,
      cancelled: groupClassSlot.cancelled === true,
    };
  }

  if (bookedSlot) {
    return {
      startTime: time,
      endTime,
      type: 'booked',
      slots,
      customerName: bookedSlot.booking?.customer_full_name || '',
      serviceName: bookedSlot.booking?.service_name || bookedSlot.service?.name || '',
    };
  }

  if (availableSlots.length > 0) {
    // When all open slots in this group share one service (typical for group
    // classes), prefer that name over the generic "N svc" count.
    const distinctServiceNames = new Set(
      availableSlots.map(s => s.service?.name).filter((n): n is string => !!n)
    );
    return {
      startTime: time,
      endTime,
      type: 'available',
      slots,
      serviceCount: availableSlots.length,
      serviceName: distinctServiceNames.size === 1 ? Array.from(distinctServiceNames)[0] : undefined,
    };
  }

  return {
    startTime: time,
    endTime,
    type: 'blocked',
    slots,
  };
};

/** Can two consecutive merged blocks fuse into one? */
const canMergeBlocks = (block1: MergedTimeBlock, block2: MergedTimeBlock): boolean => {
  if (block1.type !== block2.type) return false;

  if (block1.type === 'available') {
    return block1.serviceCount === block2.serviceCount
        && block1.serviceName === block2.serviceName;
  }

  if (block1.type === 'booked') {
    return block1.customerName === block2.customerName
        && block1.serviceName === block2.serviceName;
  }

  // Group classes never merge — each instance stands alone.
  if (block1.type === 'group_class') return false;

  return true;
};

const timeToMinutes = (time: string): number => {
  const [hours, mins] = time.split(':').map(Number);
  return hours * 60 + mins;
};

const blocksOverlap = (a: MergedTimeBlock, b: MergedTimeBlock): boolean => {
  const aStart = timeToMinutes(a.startTime);
  const aEnd = timeToMinutes(a.endTime);
  const bStart = timeToMinutes(b.startTime);
  const bEnd = timeToMinutes(b.endTime);
  return aStart < bEnd && bStart < aEnd;
};

// ── Sub-components ─────────────────────────────────────────────────────

/** Day-view: a vertically-stacked time row grouping all slots that share a
 *  start time (booked / available / blocked / mixed). */
export interface TimeSlotGroupProps {
  time: string;
  slots: StaffAvailableSlot[];
  density: 'compact' | 'comfortable' | 'spacious';
  onViewBooking: (slot: StaffAvailableSlot) => void;
  onBlockSlot: (slotId: number) => void;
  onUnblockSlot: (slotId: number) => void;
  onDeleteSlot: (slotId: number) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any;
}

export const TimeSlotGroup = ({
  time,
  slots,
  density,
  onViewBooking,
  onBlockSlot,
  onUnblockSlot,
  onDeleteSlot,
  t,
}: TimeSlotGroupProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const bookedSlot = slots.find(s => s.booked);
  const availableSlots = slots.filter(s => s.available && !s.booked);
  const blockedSlots = slots.filter(s => !s.available && !s.booked);
  const availableCount = availableSlots.length;

  const paddingClass = density === 'compact' ? 'p-2' : density === 'spacious' ? 'p-5' : 'p-3';
  const textSize = density === 'compact' ? 'text-xs' : density === 'spacious' ? 'text-base' : 'text-sm';

  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <div className={cn("flex items-start gap-4 hover:bg-gray-50 transition-colors", paddingClass)}>
        {/* Time Label - Fixed Width */}
        <div className="w-20 flex-shrink-0 pt-1">
          <span className={cn("font-semibold text-gray-900", textSize)}>
            {format(parseISO(`2000-01-01T${time}`), 'h:mm a')}
          </span>
        </div>

        {/* Status Content */}
        <div className="flex-1 min-w-0">
          {bookedSlot ? (
            // BOOKED STATE
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="w-2.5 h-2.5 rounded-full bg-brand-500 flex-shrink-0" />
                <span className={cn("font-semibold text-blue-900", textSize)}>
                  {t('schedule.status.booked', 'BOOKED')}
                </span>
                <span className={cn("text-gray-700", textSize)}>
                  {bookedSlot.service?.name}
                </span>
                {bookedSlot.booking?.customer_full_name && (
                  <>
                    <span className="text-gray-400">—</span>
                    <span className={cn("font-medium text-gray-900", textSize)}>
                      {bookedSlot.booking.customer_full_name}
                    </span>
                  </>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onViewBooking(bookedSlot)}
                  className="ml-auto text-brand-500 hover:text-blue-700 hover:bg-brand-100 hover:bg-brand-500-50 hover:bg-brand-500-900/20 h-8"
                >
                  {t('schedule.actions.viewBooking', 'View')}
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>

              {/* Time range */}
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <Clock className="h-3.5 w-3.5" />
                <span>
                  {format(parseISO(bookedSlot.start_time!), 'HH:mm')} - {format(parseISO(bookedSlot.end_time!), 'HH:mm')}
                </span>
                <Badge variant="outline" className="text-[10px] bg-blue-50 border-brand-500 text-blue-700">
                  {bookedSlot.booking?.status || 'confirmed'}
                </Badge>
              </div>

              {/* Warning about blocked slots */}
              {slots.length > 1 && (
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                  <span className="text-xs text-amber-800">
                    {slots.length - 1} {t('schedule.overlapping.blockedAtTime', 'other service(s) blocked at this time')}
                  </span>
                </div>
              )}
            </div>
          ) : availableCount > 0 ? (
            // AVAILABLE STATE
            <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-600 flex-shrink-0" />
                <span className={cn("font-semibold text-emerald-900", textSize)}>
                  {t('schedule.status.available', 'AVAILABLE')}
                </span>
                <Badge className="bg-emerald-100 text-emerald-800 border-0">
                  {availableCount} {t('schedule.services', 'service')}{availableCount > 1 ? 's' : ''}
                </Badge>

                <CollapsibleTrigger asChild>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="ml-auto text-gray-600 hover:text-gray-900 h-8"
                  >
                    {isExpanded ? t('schedule.collapse', 'Collapse') : t('schedule.expand', 'Details')}
                    <ChevronRight className={cn("ml-1 h-4 w-4 transition-transform", isExpanded && "rotate-90")} />
                  </Button>
                </CollapsibleTrigger>
              </div>

              {/* Service chips - always visible */}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {availableSlots.slice(0, isExpanded ? availableSlots.length : 3).map(slot => (
                  <div
                    key={slot.id}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-md text-xs text-gray-700"
                  >
                    <Package className="h-3 w-3" />
                    {slot.service?.name}
                    <span className="text-gray-500">
                      ({slot.end_time && slot.start_time ?
                        Math.round((new Date(slot.end_time).getTime() - new Date(slot.start_time).getTime()) / 60000)
                        : '?'}min)
                    </span>
                  </div>
                ))}
                {!isExpanded && availableSlots.length > 3 && (
                  <div className="inline-flex items-center px-2 py-1 text-xs text-gray-500">
                    +{availableSlots.length - 3} {t('schedule.more', 'more')}
                  </div>
                )}
              </div>

              {/* Expanded details with actions */}
              <CollapsibleContent>
                <div className="mt-3 space-y-2 pl-3 border-l-2 border-gray-200">
                  {availableSlots.map(slot => (
                    <div
                      key={slot.id}
                      className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-gray-900 truncate">
                          {slot.service?.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {format(parseISO(slot.start_time!), 'HH:mm')} - {format(parseISO(slot.end_time!), 'HH:mm')}
                        </div>
                      </div>

                      <div className="flex gap-1 ml-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onBlockSlot(slot.id)}
                          className="h-8 w-8 p-0 text-amber-600 hover:bg-amber-50"
                          title={t('schedule.actions.block', 'Block')}
                        >
                          <Ban className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onDeleteSlot(slot.id)}
                          className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                          title={t('schedule.actions.delete', 'Delete')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}

                  {/* Show blocked slots too if any */}
                  {blockedSlots.length > 0 && (
                    <div className="pt-2 mt-2 border-t border-gray-200">
                      <p className="text-xs text-gray-500 mb-2">
                        {t('schedule.blockedSlots', 'Blocked slots')}:
                      </p>
                      {blockedSlots.map(slot => (
                        <div
                          key={slot.id}
                          className="flex items-center justify-between p-2 bg-gray-100 border border-gray-300 rounded-lg"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm text-gray-600 truncate">
                              {slot.service?.name}
                            </div>
                          </div>
                          <div className="flex gap-1 ml-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => onUnblockSlot(slot.id)}
                              className="h-8 w-8 p-0 text-emerald-600 hover:bg-emerald-50"
                              title={t('schedule.actions.unblock', 'Unblock')}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => onDeleteSlot(slot.id)}
                              className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                              title={t('schedule.actions.delete', 'Delete')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          ) : (
            // ALL BLOCKED STATE
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-gray-400 flex-shrink-0" />
              <span className={cn("font-medium text-gray-600", textSize)}>
                {t('schedule.status.blocked', 'BLOCKED')}
              </span>
              <Badge variant="secondary" className="text-xs">
                {blockedSlots.length} {t('schedule.services', 'service')}{blockedSlots.length > 1 ? 's' : ''}
              </Badge>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setIsExpanded(!isExpanded)}
                className="ml-auto text-gray-600 hover:text-gray-900 h-8"
              >
                {t('schedule.actions.manage', 'Manage')}
                <ChevronRight className={cn("ml-1 h-4 w-4 transition-transform", isExpanded && "rotate-90")} />
              </Button>
            </div>
          )}

          {/* Expanded blocked slots management (when all blocked) */}
          {!bookedSlot && availableCount === 0 && blockedSlots.length > 0 && isExpanded && (
            <div className="mt-3 space-y-2 pl-3 border-l-2 border-gray-300">
              {blockedSlots.map(slot => (
                <div
                  key={slot.id}
                  className="flex items-center justify-between p-2 bg-gray-100 border border-gray-300 rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-gray-600 truncate">
                      {slot.service?.name}
                    </div>
                  </div>
                  <div className="flex gap-1 ml-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onUnblockSlot(slot.id)}
                      className="h-8 w-8 p-0 text-emerald-600 hover:bg-emerald-50"
                      title={t('schedule.actions.unblock', 'Unblock')}
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onDeleteSlot(slot.id)}
                      className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                      title={t('schedule.actions.delete', 'Delete')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/** Week-view: per-row rendering of all slots at a given start time across one day column. */
export interface WeekTimeRowProps {
  time: string;
  slots: StaffAvailableSlot[];
  onViewBooking: (slot: StaffAvailableSlot) => void;
  onBlockSlot: (slotId: number) => void;
  onUnblockSlot: (slotId: number) => void;
  onDeleteSlot: (slotId: number) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any;
}

export const WeekTimeRow = ({
  time,
  slots,
  onViewBooking,
  onBlockSlot,
  onUnblockSlot,
  onDeleteSlot,
  t,
}: WeekTimeRowProps) => {
  const bookedSlot = slots.find(s => s.booked);
  const availableSlots = slots.filter(s => s.available && !s.booked);
  const blockedSlots = slots.filter(s => !s.available && !s.booked);
  const availableCount = availableSlots.length;

  const formatTimeDisplay = (timeStr: string) => {
    return format(parseISO(`2000-01-01T${timeStr}`), 'HH:mm');
  };

  // BOOKED - Blue block with customer name and service
  if (bookedSlot) {
    return (
      <button
        onClick={() => onViewBooking(bookedSlot)}
        className="w-full px-2 py-1.5 bg-brand-100 rounded-md border-l-[3px] border-brand-500 hover:bg-brand-100 hover:bg-brand-500-100 hover:bg-brand-500-900/30 transition-all text-left group"
      >
        <div className="text-[10px] text-brand-500 font-semibold uppercase tracking-wide">
          {formatTimeDisplay(time)} • {t('schedule.status.booked', 'Booked')}
        </div>
        <div className="text-xs font-bold text-blue-900 truncate mt-0.5">
          {bookedSlot.booking?.customer_full_name || t('schedule.customer', 'Customer')}
        </div>
        <div className="text-[10px] text-brand-500 truncate">
          {bookedSlot.service?.name}
        </div>
      </button>
    );
  }

  // AVAILABLE - Green block with service count (with popover for details)
  if (availableCount > 0) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button className="w-full px-2 py-1.5 bg-emerald-50 rounded-md border-l-[3px] border-emerald-500 shadow-sm shadow-emerald-500/20 hover:bg-emerald-100 hover:shadow-md hover:shadow-emerald-500/30 transition-all text-left">
            <div className="text-[10px] text-emerald-600 font-semibold uppercase tracking-wide">
              {formatTimeDisplay(time)} • {t('schedule.status.available', 'Available')}
            </div>
            <div className="text-xs font-bold text-emerald-900 mt-0.5">
              {t('schedule.open', 'Open')}
            </div>
            <div className="text-[10px] text-emerald-700 flex items-center gap-1">
              <Layers className="h-3 w-3" />
              {availableCount} {t('schedule.service', 'service')}{availableCount > 1 ? 's' : ''}
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-72 p-0"
          side="right"
          align="start"
          sideOffset={8}
        >
          <div className="px-3 py-2 border-b bg-emerald-50">
            <div className="font-semibold text-emerald-900 text-sm">
              {formatTimeDisplay(time)}
            </div>
            <div className="text-xs text-emerald-700">
              {availableCount} {t('schedule.servicesAvailable', 'services available')}
            </div>
          </div>

          <div className="p-2 space-y-1.5 max-h-[250px] overflow-y-auto">
            {availableSlots.map(slot => (
              <div
                key={slot.id}
                className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-lg"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {slot.service?.name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {slot.end_time && slot.start_time ?
                      Math.round((new Date(slot.end_time).getTime() - new Date(slot.start_time).getTime()) / 60000)
                      : '?'} min
                  </div>
                </div>
                <div className="flex gap-1 ml-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onBlockSlot(slot.id)}
                    className="h-7 w-7 p-0 text-amber-600 hover:bg-amber-50"
                    title={t('schedule.actions.block', 'Block')}
                  >
                    <Ban className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onDeleteSlot(slot.id)}
                    className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                    title={t('schedule.actions.delete', 'Delete')}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}

            {blockedSlots.length > 0 && (
              <>
                <div className="pt-2 mt-2 border-t border-gray-200">
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-1.5">
                    {t('schedule.blocked', 'Blocked')} ({blockedSlots.length})
                  </div>
                </div>
                {blockedSlots.map(slot => (
                  <div
                    key={slot.id}
                    className="flex items-center justify-between p-2 bg-gray-100 border border-gray-300 rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-600 truncate">
                        {slot.service?.name}
                      </div>
                    </div>
                    <div className="flex gap-1 ml-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onUnblockSlot(slot.id)}
                        className="h-7 w-7 p-0 text-emerald-600 hover:bg-emerald-50"
                        title={t('schedule.actions.unblock', 'Unblock')}
                      >
                        <Check className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onDeleteSlot(slot.id)}
                        className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                        title={t('schedule.actions.delete', 'Delete')}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  // BLOCKED - Gray block (all slots blocked at this time)
  return (
    <Popover>
      <PopoverTrigger asChild>
        <div className="w-full px-2 py-1.5 bg-gray-100 rounded-md border-l-[3px] border-gray-400 cursor-pointer hover:bg-gray-200 transition-all">
          <div className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide">
            {formatTimeDisplay(time)} • {t('schedule.status.blocked', 'Blocked')}
          </div>
          <div className="text-[10px] text-gray-600 flex items-center gap-1 mt-0.5">
            <Lock className="h-3 w-3" />
            {t('schedule.unavailable', 'Unavailable')}
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-72 p-0"
        side="right"
        align="start"
        sideOffset={8}
      >
        <div className="px-3 py-2 border-b bg-gray-100">
          <div className="font-semibold text-gray-700 text-sm">
            {formatTimeDisplay(time)}
          </div>
          <div className="text-xs text-gray-500">
            {blockedSlots.length} {t('schedule.blockedSlots', 'blocked slots')}
          </div>
        </div>

        <div className="p-2 space-y-1.5 max-h-[250px] overflow-y-auto">
          {blockedSlots.map(slot => (
            <div
              key={slot.id}
              className="flex items-center justify-between p-2 bg-gray-100 border border-gray-300 rounded-lg"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-600 truncate">
                  {slot.service?.name}
                </div>
              </div>
              <div className="flex gap-1 ml-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onUnblockSlot(slot.id)}
                  className="h-7 w-7 p-0 text-emerald-600 hover:bg-emerald-50"
                  title={t('schedule.actions.unblock', 'Unblock')}
                >
                  <Check className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onDeleteSlot(slot.id)}
                  className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                  title={t('schedule.actions.delete', 'Delete')}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

/** Week/month grid block — compact single-block renderer with status-based styling. */
export interface WeekCalendarBlockProps {
  block: MergedTimeBlock;
  onViewBooking: (slot: StaffAvailableSlot) => void;
  onBlockSlot: (slotId: number) => void;
  onUnblockSlot: (slotId: number) => void;
  onDeleteSlot: (slotId: number) => void;
  // Group-class blocks bypass the per-slot popover and route to a dedicated
  // edit/cancel dialog at the page level.
  onEditGroupClass?: (slot: StaffAvailableSlot) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any;
}

export const WeekCalendarBlock = ({
  block,
  onViewBooking,
  onBlockSlot,
  onUnblockSlot,
  onDeleteSlot,
  onEditGroupClass,
  t,
}: WeekCalendarBlockProps) => {
  const availableSlots = block.slots.filter(s => s.available && !s.booked);
  const blockedSlots = block.slots.filter(s => !s.available && !s.booked);

  // GROUP CLASS - Violet block, "Service Name N/M". Cancelled classes get
  // a gray strikethrough state with a "Cancelled" label.
  if (block.type === 'group_class') {
    const groupClassSlot = block.slots.find(isGroupClassSlot);
    const isCancelled = block.cancelled === true;
    return (
      <button
        type="button"
        onClick={() => groupClassSlot && onEditGroupClass?.(groupClassSlot)}
        className={cn(
          'w-full h-full border-l-[3px] px-1.5 py-1 text-left transition-colors overflow-hidden',
          isCancelled
            ? 'bg-slate-100 border-slate-400 hover:bg-slate-200'
            : 'bg-violet-50 border-violet-500 hover:bg-violet-100'
        )}
      >
        <div className={cn(
          'text-[11px] font-semibold truncate flex items-center gap-1',
          isCancelled ? 'text-slate-500 line-through' : 'text-violet-700'
        )}>
          <Users className="h-2.5 w-2.5 flex-shrink-0" />
          <span className="truncate">{block.serviceName || t('schedule.customer', 'Class')}</span>
          {!isCancelled && (
            <span className="ml-auto tabular-nums">{block.currentBookings}/{block.maxCapacity}</span>
          )}
        </div>
        <div className={cn(
          'text-[10px] tabular-nums',
          isCancelled ? 'text-slate-500' : 'text-violet-600'
        )}>
          {isCancelled
            ? t('staff:availability.groupClass.cancelledBadge', 'Cancelled')
            : `${block.startTime}–${block.endTime}`}
        </div>
      </button>
    );
  }

  // BOOKED - Blue block
  if (block.type === 'booked') {
    const bookedSlot = block.slots.find(s => s.booked);
    return (
      <button
        onClick={() => bookedSlot && onViewBooking(bookedSlot)}
        className="w-full h-full bg-brand-100 border-l-[3px] border-brand-500 text-left hover:bg-brand-100 hover:bg-brand-500-200 hover:bg-brand-500-900/60 transition-colors"
        style={{ padding: '4px 6px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
      >
        <span className="text-[11px] text-brand-500 font-semibold truncate block">
          {block.customerName || t('schedule.customer', 'Customer')}
        </span>
        <span className="text-[10px] text-brand-500 truncate block">
          {block.serviceName}
        </span>
      </button>
    );
  }

  // AVAILABLE - Green block. Group classes (single slot with max_capacity > 1)
  // bypass the popover entirely and route a click to the page-level edit
  // dialog — the user wants "click to edit", not a hover menu.
  if (block.type === 'available') {
    const groupClassSlot = availableSlots.find(
      (s) => (s.max_capacity ?? s.service?.max_capacity ?? 1) > 1
    );
    const isGroupClass = !!groupClassSlot && availableSlots.length === 1;

    const headerLabel = block.serviceName ?? t('schedule.open', 'Open');

    if (isGroupClass) {
      const cap = groupClassSlot.max_capacity ?? groupClassSlot.service?.max_capacity ?? 0;
      const booked = groupClassSlot.current_bookings ?? 0;
      return (
        <button
          type="button"
          onClick={() => onEditGroupClass?.(groupClassSlot)}
          className="w-full h-full bg-violet-50 border-l-[3px] border-violet-500 px-1.5 py-1 text-left hover:bg-violet-100 transition-colors overflow-hidden"
        >
          <div className="text-[10px] text-violet-700 font-semibold truncate flex items-center gap-1">
            <Users className="h-2.5 w-2.5 flex-shrink-0" />
            <span className="truncate">{groupClassSlot.service?.name ?? headerLabel}</span>
          </div>
          <div className="text-[9px] text-violet-600 tabular-nums">
            {booked}/{cap} · {block.startTime}–{block.endTime}
          </div>
        </button>
      );
    }

    return (
      <Popover>
        <PopoverTrigger asChild>
          <button className="w-full h-full bg-emerald-50 border-l-[3px] border-emerald-500 px-1.5 py-1 text-left hover:bg-emerald-100 transition-colors overflow-hidden">
            <div className="text-[10px] text-emerald-700 font-semibold truncate">
              {headerLabel}
            </div>
            <div className="text-[9px] text-emerald-600">
              {block.serviceName
                ? `${block.startTime} – ${block.endTime}`
                : `${block.serviceCount} ${t('schedule.svc', 'svc')}`}
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-0" side="right" align="start" sideOffset={4}>
          <div className="px-3 py-2 border-b bg-emerald-50">
            <div className="text-sm font-semibold text-emerald-900">
              {t('schedule.status.available', 'Available')}
            </div>
            <div className="text-xs text-emerald-700">
              {block.serviceCount} {t('schedule.servicesAvailable', 'services')}
            </div>
          </div>
          <div className="p-2 space-y-1 max-h-[200px] overflow-y-auto">
            {availableSlots.slice(0, 4).map(slot => (
              <div key={slot.id} className="flex items-center justify-between p-1.5 bg-white rounded border">
                <span className="text-xs text-gray-900 truncate">{slot.service?.name}</span>
                <div className="flex gap-0.5">
                  <Button size="sm" variant="ghost" onClick={() => onBlockSlot(slot.id)} className="h-6 w-6 p-0 text-amber-600">
                    <Ban className="h-3 w-3" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => onDeleteSlot(slot.id)} className="h-6 w-6 p-0 text-red-600">
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  // BLOCKED - Gray block with popover
  return (
    <Popover>
      <PopoverTrigger asChild>
        <div className="w-full h-full bg-gray-100 border-l-[3px] border-gray-400 px-1.5 py-1 cursor-pointer hover:bg-gray-200 transition-colors overflow-hidden">
          <div className="text-[10px] text-gray-500 font-medium flex items-center gap-1">
            <Lock className="h-3 w-3" />
            {t('schedule.status.blocked', 'Blocked')}
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" side="right" align="start" sideOffset={4}>
        <div className="px-3 py-2 border-b bg-gray-100">
          <div className="text-sm font-semibold text-gray-700">
            {t('schedule.status.blocked', 'Blocked')}
          </div>
          <div className="text-xs text-gray-500">
            {blockedSlots.length} {t('schedule.slots', 'slots')}
          </div>
        </div>
        <div className="p-2 space-y-1 max-h-[200px] overflow-y-auto">
          {blockedSlots.slice(0, 4).map(slot => (
            <div key={slot.id} className="flex items-center justify-between p-1.5 bg-gray-50 rounded border">
              <span className="text-xs text-gray-600 truncate">{slot.service?.name}</span>
              <div className="flex gap-0.5">
                <Button size="sm" variant="ghost" onClick={() => onUnblockSlot(slot.id)} className="h-6 w-6 p-0 text-emerald-600">
                  <Check className="h-3 w-3" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => onDeleteSlot(slot.id)} className="h-6 w-6 p-0 text-red-600">
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

